import React from 'react';
import { MapPin, ChevronRight } from 'lucide-react';

const ServiceAreas = ({ compact = false }) => {
  const serviceRegions = [
    {
      title: 'New York City Area',
      icon: '🗽',
      areas: ['Brooklyn', 'Queens', 'Staten Island', 'Manhattan', 'The Bronx']
    },
    {
      title: 'Nassau County',
      icon: '🏘️',
      areas: [
        'Hempstead', 'Garden City', 'Long Beach', 'Mineola', 'Westbury',
        'Levittown', 'Great Neck', 'Glen Cove', 'Rockville Centre'
      ]
    },
    {
      title: 'Suffolk County',
      icon: '🏖️',
      areas: [
        'Babylon', 'Huntington', 'Smithtown', 'Brookhaven', 'Patchogue',
        'Port Jefferson', 'Stony Brook', 'Riverhead', 'Southampton', 'Montauk'
      ]
    },
    {
      title: 'Westchester County',
      icon: '🌳',
      areas: ['Yonkers', 'White Plains', 'Mount Kisco', 'Peekskill']
    },
    {
      title: 'Putnam & Dutchess Counties',
      icon: '⛰️',
      areas: ['Carmel', 'Brewster', 'Poughkeepsie', 'Fishkill']
    },
    {
      title: 'Fairfield County, CT',
      icon: '🌊',
      areas: ['Stamford', 'Greenwich', 'Norwalk', 'Bridgeport', 'Danbury']
    },
    {
      title: 'Hudson County, NJ',
      icon: '🌆',
      areas: ['Jersey City', 'Hoboken', 'Union City']
    },
    {
      title: 'Bergen County, NJ',
      icon: '🏙️',
      areas: ['Fort Lee', 'Englewood', 'Teaneck', 'Hackensack']
    }
  ];

  if (compact) {
    return (
      <div data-testid="service-areas-compact" className="space-y-4">
        <h4 className="text-lg font-semibold mb-4 flex items-center">
          <MapPin className="text-red-500 mr-2" size={20} />
          Service Areas
        </h4>
        <p className="text-gray-400 text-sm leading-relaxed">
          We proudly serve <strong className="text-white">New York City</strong>, 
          <strong className="text-white"> Long Island</strong>, 
          lower <strong className="text-white">Connecticut</strong>, and 
          nearby <strong className="text-white"> New Jersey</strong>. 
          Mobile detailing to your home or office.
        </p>
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
          <div>• NYC (5 Boroughs)</div>
          <div>• Nassau County</div>
          <div>• Suffolk County</div>
          <div>• Westchester County</div>
          <div>• Fairfield County, CT</div>
          <div>• Hudson & Bergen, NJ</div>
        </div>
      </div>
    );
  }

  return (
    <section data-testid="service-areas-section" className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-600 to-red-800 rounded-2xl mb-6">
            <MapPin className="text-white" size={32} />
          </div>
          <h2 
            data-testid="service-areas-title"
            className="text-4xl lg:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Our <span className="text-red-500">Service Areas</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            We proudly serve <strong className="text-white">New York City</strong>, 
            <strong className="text-white"> Long Island</strong>, 
            lower <strong className="text-white">Connecticut</strong>, and 
            nearby <strong className="text-white"> New Jersey</strong>. 
            Mobile detailing to your home or office.
          </p>
        </div>

        {/* Service Areas Grid */}
        <div data-testid="service-areas-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {serviceRegions.map((region, index) => (
            <div
              key={index}
              data-testid={`service-area-${index}`}
              className="glass-card p-6 hover:scale-105 transition-transform duration-300"
            >
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">{region.icon}</span>
                <h3 className="text-lg font-semibold text-white">{region.title}</h3>
              </div>
              <ul className="space-y-2">
                {region.areas.map((area, areaIndex) => (
                  <li key={areaIndex} className="flex items-start text-sm text-gray-400">
                    <ChevronRight className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="ml-1">{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Coverage Note */}
        <div data-testid="coverage-note" className="mt-12 text-center">
          <div className="glass-card p-8 max-w-4xl mx-auto">
            <p className="text-gray-300 text-lg mb-4">
              <strong className="text-red-500">Not sure if we serve your area?</strong>
            </p>
            <p className="text-gray-400">
              We're continuously expanding our service coverage. Contact us to check if we can accommodate your location. 
              We're committed to bringing premium mobile detailing services to more communities across the tri-state area.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreas;
